import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompiler,
    SketchConstraintFormulaDispatchResult,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';

/**
 * 草图关系公式注册表。
 *
 * 职责：
 * 1. 按关系类型维护可扩展的公式编译器链；
 * 2. 在同一关系存在多个合法目标族时按注册顺序尝试；
 * 3. 把未注册与已注册但不适用明确区分，支持迁移期逐族收口。
 */
export class SketchConstraintFormulaRegistry {
    /** 关系类型到公式编译器链。 */
    private readonly compilerMap = new Map<string, SketchConstraintFormulaCompiler[]>();

    /**
     * 创建公式注册表。
     *
     * @param compilers 初始公式编译器集合。
     */
    public constructor(compilers: readonly SketchConstraintFormulaCompiler[] = []) {
        compilers.forEach((compiler) => this.register(compiler));
    }

    /**
     * 注册一个公式编译器。
     *
     * 同一编译器实例不得对同一关系类型重复注册，避免一次关系重复生成方程；
     * 不同编译器可以共同处理同一关系类型的不同目标组合。
     *
     * @param compiler 公式编译器。
     */
    public register(compiler: SketchConstraintFormulaCompiler): void {
        compiler.relationKinds.forEach((relationKind) => {
            const compilers = this.compilerMap.get(relationKind) ?? [];

            if (compilers.includes(compiler)) {
                throw new Error(`关系公式编译器重复注册：${relationKind}`);
            }

            compilers.push(compiler);
            this.compilerMap.set(relationKind, compilers);
        });
    }

    /**
     * 编译一条草图关系。
     *
     * @param context 关系公式编译上下文。
     * @returns 分派结果；未注册时由迁移期调用方继续处理尚未提取的关系族。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaDispatchResult {
        const compilers = this.compilerMap.get(context.constraintState.relationKind);

        if (!compilers || compilers.length === 0) {
            return 'not-registered';
        }

        for (const compiler of compilers) {
            const result = compiler.compile(context);

            if (result !== 'not-applicable') {
                return result;
            }
        }

        return 'not-registered';
    }

    /**
     * 获取当前已登记的关系类型快照。
     *
     * @returns 去重并按字典序排列的关系类型。
     */
    public getRegisteredRelationKinds(): readonly string[] {
        return [...this.compilerMap.keys()].sort((left, right) => {
            if (left === right) {
                return 0;
            }
            return left < right ? -1 : 1;
        });
    }
}
